/**
 * openingPositions — résolution des « Postes ouverts » pilotés par le bilan.
 *
 * Source unique partagée par l'onglet Postes ouverts (édition) ET le moteur de
 * trésorerie (calcul) : à partir des positions stockées (par compte : inclusion
 * + profil de réalisation 12 mois) et de la vue bilan de l'année d'arrêté, on
 * résout, pour chaque compte des sections « Actifs circulants » et « Dettes
 * court terme », son solde d'ouverture (lu en direct dans le bilan) et son
 * affectation au cash (encaissement / décaissement / solde initial).
 *
 * Date d'arrêté = solde de fin du mois PRÉCÉDANT le 1er mois du forecast.
 * Le montant n'est jamais stocké : donnée absente au bilan = absente ici.
 */

const ASSET_SECTION = 'current_assets';
const LIABILITY_SECTION = 'current_liabilities';

// La ligne « Liquidité » alimente le solde initial, pas un flux. Identifiée par
// sa clé standard (std_key) portée par le bilan — jamais par son libellé, qui
// reste cosmétique et renommable.
export const isLiquidityRow = (row) => row?.std_key === 'liquidity';

/**
 * Période d'arrêté du bilan : le mois précédant le 1er mois du forecast.
 * start_month est 0-indexé (0 = janvier). month renvoyé est 1-indexé (1..12).
 */
export function openingPeriod(scenario) {
  const sm = Number.isInteger(scenario?.start_month) ? scenario.start_month : 0;
  const sy = Number(scenario?.start_year) || new Date().getFullYear();
  if (sm === 0) return { year: sy - 1, month: 12 };   // janvier → décembre N-1
  return { year: sy, month: sm };                      // prev (0-idx)=sm-1 → 1-idx=sm
}

const emptyPct = () => Array(12).fill(0);

/**
 * @param {object} args
 * @param {object} args.scenario       scénario trésorerie (start_month/start_year)
 * @param {Array}  args.positions      positions stockées [{account_number, included, realization_pct, display_order}]
 * @param {object} args.bsViewByYear   { [year]: vue getBsView } — doit contenir l'année d'arrêté
 * @returns {{
 *   period: {year:number, month:number},
 *   hasView: boolean,
 *   groups: Array<{line_id, label, section, accounts: Array}>,   // hors Liquidités
 *   liquidity: {label, total, count}|null,   // pris de facto → solde initial
 *   receivableLines: Array<{id, customer_name, amount, collection_pct}>,
 *   payableLines: Array<{id, supplier_name, amount, payment_pct}>,
 *   initialBalance: number|null   // somme Liquidités (de facto), null si aucune ligne Liquidités
 * }}
 */
// Seuil sous lequel un solde est considéré nul (on n'affiche pas les comptes vides).
const ZERO = 0.005;

export function resolveOpeningPositions({ scenario, positions = [], bsViewByYear = {} }) {
  const period = openingPeriod(scenario);
  const view = bsViewByYear[period.year] || null;
  const posByAcc = new Map((positions || []).map((p) => [String(p.account_number), p]));
  const balAt = (a) => Number(a.months?.[String(period.month)]) || 0;

  const groups = [];
  const receivableLines = [];
  const payableLines = [];
  let liquidity = null;
  let liquidityAccounts = [];
  let initialBalance = 0;

  const rows = (view?.rows) || [];
  for (const row of rows) {
    if (row.type !== 'detail') continue;
    if (row.section !== ASSET_SECTION && row.section !== LIABILITY_SECTION) continue;

    // ── Liquidités : prises DE FACTO (100 %), sans détail → solde initial. ──
    // Uniquement si la ligne a de vrais comptes avec solde (bilan au niveau
    // compte). Si le bilan n'est qu'un forecast par ligne (pas de comptes), on
    // ne touche pas au solde initial saisi (pas de repli silencieux à 0).
    if (row.section === ASSET_SECTION && isLiquidityRow(row)) {
      const accts = (row.accounts || []).filter((a) => Math.abs(balAt(a)) >= ZERO);
      if (accts.length > 0) {
        const total = accts.reduce((s, a) => s + balAt(a), 0);
        initialBalance += total;
        liquidity = { label: row.label, total, count: accts.length };
        liquidityAccounts = liquidityAccounts.concat(accts.map((a) => ({
          account_number: String(a.account_number),
          account_desc: a.account_desc || '',
          balance: balAt(a),
        })));
      }
      continue;
    }

    // ── Autres postes : un compte = une ligne, MAIS seulement s'il a un solde. ──
    const groupAccounts = [];
    for (const a of (row.accounts || [])) {
      const balance = balAt(a);
      if (Math.abs(balance) < ZERO) continue;   // compte vide à l'arrêté → masqué
      const acc = String(a.account_number);
      const pos = posByAcc.get(acc);
      const included = pos ? !!pos.included : false;
      const pct = (pos && Array.isArray(pos.realization_pct) && pos.realization_pct.length === 12)
        ? pos.realization_pct.map(Number)
        : emptyPct();
      const signed = row.section === LIABILITY_SECTION ? -balance : balance;

      groupAccounts.push({
        account_number: acc,
        account_desc: a.account_desc || '',
        balance,
        signed,
        included,
        realization_pct: pct,
        realization_rule: pos ? (pos.realization_rule || null) : null,
        category: pos ? (pos.category || null) : null,
        liquidity: false,
        section: row.section,
      });

      if (!included) continue;
      if (row.section === ASSET_SECTION) {
        receivableLines.push({ id: acc, customer_name: a.account_desc || acc, amount: balance, collection_pct: pct });
      } else {
        payableLines.push({ id: acc, supplier_name: a.account_desc || acc, amount: balance, payment_pct: pct });
      }
    }

    // Poste sans aucun compte à solde non nul → on ne l'affiche pas.
    if (groupAccounts.length > 0) {
      groups.push({
        line_id: row.code,
        label: row.label,
        section: row.section,
        accounts: groupAccounts,
      });
    }
  }

  const manualLines = (positions || []).filter((p) => p.source === 'manual');

  return {
    period,
    hasView: !!view,
    groups,
    liquidity,
    liquidityAccounts,
    manualLines,
    receivableLines,
    payableLines,
    initialBalance: liquidity ? initialBalance : null,
  };
}
